import {
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
} from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { unionBy } from 'lodash-es';
import type { FileInputDto } from '../file/model';
import type { ScriptDto } from '../script/script.dto';
import {
  TaskStatusCode,
  type ImageGenerationTaskDto,
  type ProductAnalysisTaskDto,
} from '../task/model';
import { GenerationType } from '../video-model/model-spec';
import { ScriptSource } from '../video/model';
import type { ProductAnalysisShotDto, ProductScriptFormValues } from './model';

export const MAX_PRODUCT_VIDEO_REFERENCE_IMAGES = 9;

export interface ProductShotImageTaskRecord {
  shot_position: number;
  initial_task: ImageGenerationTaskDto;
}

type ProductAnalysisTaskTiming = Pick<
  ProductAnalysisTaskDto,
  'created_at' | 'status' | 'updated_at'
>;

export function formatProductAnalysisTaskElapsed(
  task: ProductAnalysisTaskTiming,
) {
  const is_settled =
    task.status === TaskStatusCode.任务成功 ||
    task.status === TaskStatusCode.任务失败;

  if (!is_settled) {
    return `已进行 ${formatDistanceToNow(task.created_at, {
      locale: zhCN,
      includeSeconds: true,
    })}`;
  }

  return (
    formatDuration(
      intervalToDuration({
        start: task.created_at,
        end: task.updated_at ?? Date.now(),
      }),
      { locale: zhCN },
    ) || '0 秒'
  );
}

export function appendProductShotImageTask(
  tasks: ProductShotImageTaskRecord[],
  task: ProductShotImageTaskRecord,
) {
  return tasks.some(
    (current) => current.initial_task.id === task.initial_task.id,
  )
    ? tasks
    : [...tasks, task];
}

export function removeProductShotImageTask(
  tasks: ProductShotImageTaskRecord[],
  task_id: string,
) {
  return tasks.filter((task) => task.initial_task.id !== task_id);
}

export function buildProductAnalysisVideoPageData(input: {
  id: string;
  product_name: string;
  product_images: FileInputDto[];
  generated_images: FileInputDto[];
  shots: ProductAnalysisShotDto[];
  form_values: ProductScriptFormValues;
}): ScriptDto & {
  simple_mode: true;
  source: ScriptSource.爆款生成 | undefined;
} {
  const total_duration = input.shots.reduce(
    (total, shot) => total + shot.duration,
    0,
  );

  return {
    id: input.id,
    template_id: null,
    duration: total_duration,
    prompt: `## 镜头脚本\n\n${input.form_values.shots
      .map((shot) => shot.script.trim())
      .join('\n\n')}`,
    file_list: unionBy(
      [...input.product_images, ...input.generated_images],
      (item) => item.url,
    ),
    generation_type: GenerationType.Reference,
    aspect_ratio: '9:16',
    product_name: input.product_name,
    resolution: '720p',
    size: '720x1280',
    avatar_ids: [],
    simple_mode: true,
    source: ScriptSource.爆款生成,
    generate_audio: true,
  };
}
